import { Report, ReportWidgetDataPayload } from '@/services/api/resources/analytics-builder/model';
import {
  createAuthorizedPostRequest,
  setBody,
  fetchApi,
  decodeId,
  Versioned,
  addVersionForUpdate,
  createAuthorizedGetRequest,
  decodeVersioned,
  createAuthorizedPutRequest,
  createAuthorizedDeleteRequest,
  setQuery,
  decode,
  fetchUnparsedApi,
  createGetRequest,
  jsonHeaders,
} from '@/services/api';
import { chain, fromIOEither, map } from 'fp-ts/lib/TaskEither';
import { formCodec, formEditCodec, reportCodec, listCodec, widgetDataPayload, filterOptionsCodec } from './codec';
import { pipe } from 'fp-ts/lib/function';
import { flow } from 'fp-ts/function';
import { AnalyticsQuery, encodeAnalyticsQuery } from '@/services/api/resources/analytics-builder/query';
import { resourceBulkDelete } from '@/services/api/resources/bulkDelete';
import { SupportedExportFormat } from '@/services/api/resources/analytics';
import { getToken } from '@/services/token';

const baseUrl = '/v2/analytics-reports';
const recordUrl = (id: number): string => `${baseUrl}/${id}`;
const reportBaseUrl = '/v2/analytics/export-report';
const bulkReportBaseUrl = '/v2/analytics/export-report-bulk';
const widgetBaseUrl = '/v2/analytics/export-widget';
const detailedViewBaseUrl = '/v2/analytics/export-detailed-view';
const publicRecordUrl = (id: number): string => `/v2/public/analytics-reports/${id}`;
const publicExportUrl = (
  id: number,
  format: SupportedExportFormat,
): string => `/v2/public/analytics/export-report/${format}/${id}`;
const publicFiltersUrl = (id: number): string => `/v2/public/analytics/filters/${id}`;

export const list = (query: AnalyticsQuery) => pipe(
  baseUrl,
  createAuthorizedGetRequest,
  map(setQuery(encodeAnalyticsQuery(query))),
  chain(fetchApi),
  chain(decode(listCodec)),
);

export const get = flow(
  recordUrl,
  createAuthorizedGetRequest,
  chain(fetchApi),
  chain(decodeVersioned(reportCodec)),
);

export const publicGet = flow(
  publicRecordUrl,
  jsonHeaders(createGetRequest),
  fetchApi,
  chain(decodeVersioned(reportCodec)),
);

export const create = (report: Report) => pipe(
  baseUrl,
  createAuthorizedPostRequest,
  map(setBody(formCodec.encode(report))),
  chain(fetchApi),
  chain(decodeId),
);

export const update = (id: number, report: Versioned<Report>) => pipe(
  id,
  recordUrl,
  createAuthorizedPutRequest,
  map(addVersionForUpdate(report.version)),
  map(setBody(formEditCodec.encode(report.data))),
  chain(fetchApi),
);

export const remove = flow(
  recordUrl,
  createAuthorizedDeleteRequest,
  chain(fetchApi),
);

export const bulkDelete = (records: number[]) => resourceBulkDelete(records, baseUrl);

export const exportReport = (id: number, format: SupportedExportFormat) => pipe(
  getToken(),
  fromIOEither,
  map((token: string) => `${reportBaseUrl}/${format}/${id}?token=${token}`),
  chain(createAuthorizedGetRequest),
  chain(fetchUnparsedApi),
);

export const publicExportReport = (id: number, format: SupportedExportFormat) => pipe(
  publicExportUrl(id, format),
  jsonHeaders(createGetRequest),
  fetchUnparsedApi,
);

export const bulkExportReport = (records: number[], format: SupportedExportFormat) => pipe(
  getToken(),
  fromIOEither,
  map((token: string) => `${bulkReportBaseUrl}/${format}?token=${token}`),
  chain(createAuthorizedPostRequest),
  map(setBody({ records })),
  chain(fetchUnparsedApi),
);

export const exportWidget = (id: number, format: SupportedExportFormat) => pipe(
  getToken(),
  fromIOEither,
  map((token: string) => `${widgetBaseUrl}/${format}/${id}?token=${token}`),
  chain(createAuthorizedGetRequest),
  chain(fetchUnparsedApi),
);

export const exportDetailedView = (
  payload: ReportWidgetDataPayload,
  format: SupportedExportFormat,
  timezone: string,
) => pipe(
  getToken(),
  fromIOEither,
  map((token: string) => `${detailedViewBaseUrl}/${format}?token=${token}&timezone=${timezone}`),
  chain(createAuthorizedPostRequest),
  map(setBody(widgetDataPayload.encode(payload))),
  chain(fetchUnparsedApi),
);

export const publicFilters = flow(
  publicFiltersUrl,
  jsonHeaders(createGetRequest),
  fetchApi,
  chain(decode(filterOptionsCodec)),
);
